import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  CircularProgress, 
  Alert, 
  Divider,
  ButtonBase} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Receipt as BillingIcon,
  Paid as PaidIcon,
  Pending as PendingIcon,
  ListAlt as OrdersIcon,
  Event as AppointmentIcon,
  Schedule as ScheduledIcon,
  CheckCircle as ArrivedIcon,
  Cancel as CancelledIcon
} from '@mui/icons-material';
import { AppointmentStatus } from '../../features/appointments/types/appointment.types';
import { appointmentApi } from '../../features/appointments/api/appointmentApi';
import { appointmentOrdersApi } from '../../features/appointments/api/appointmentOrdersApi';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

const HospitalDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [recentAppointments, setRecentAppointments] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRecentAppointments = async () => {
    setLoading(true);
    setError(null);
    try {
      const todayStart = dayjs().tz('Asia/Kolkata').startOf('day').toISOString();
      const todayEnd = dayjs().tz('Asia/Kolkata').endOf('day').toISOString();
      
      let allAppointments: any[] = [];
      
      // Try to fetch appointments for each status separately to avoid 400 error
      const statuses = [AppointmentStatus.CONFIRMED, AppointmentStatus.ARRIVED, AppointmentStatus.COMPLETED];
      
      for (const status of statuses) {
        try {
          const params = {
            startDate: todayStart,
            endDate: todayEnd,
            status: [status], // Wrap in array to match API type
          };
          
          console.log(`Fetching ${status} appointments:`, params);
          const response = await appointmentApi.getAll(params);
          
          if (response.data && response.data.length > 0) {
            allAppointments.push(...response.data);
            console.log(`Found ${response.data.length} ${status} appointments`);
          }
        } catch (statusError) {
          console.warn(`Failed to fetch ${status} appointments:`, statusError);
          // Continue with other statuses
        }
      }
      
      // Sort by time (most recent first)
      const sortedAppointments = allAppointments
        .sort((a, b) => (b.time || '').localeCompare(a.time || ''));
      
      console.log('Total recent appointments found:', sortedAppointments.length);
      setRecentAppointments(sortedAppointments);
    } catch (e: any) {
      console.error('Error in fetchRecentAppointments:', e);
      setRecentAppointments([]); // Set empty array instead of error
    } finally {
      setLoading(false);
    }
  };  

  useEffect(() => {
    fetchStats();
    fetchRecentAppointments();
  }, []);
type StatusCounts = {
  BILLED: number;
  PAID: number;
  PARTIALLY_PAID: number;
  ORDERED: number;
  REFUNDED: number;
  CANCELLED: number;
};

const [stats, setStats] = useState({
  totalOrders: 0,
  totalBilled: 0,
  totalPaid: 0,
  outstanding: 0,
  unbilledAmount: 0,
  statusCounts: {
    BILLED: 0,
    PAID: 0,
    PARTIALLY_PAID: 0,
    ORDERED: 0,
    REFUNDED: 0,
    CANCELLED: 0,
  } as StatusCounts,
});

const [appointmentStats, setAppointmentStats] = useState({
  totalAppointments: 0,
  totalScheduled: 0,
  totalArrived: 0,
  totalCancelledNoShow: 0,
});


  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const today = dayjs().tz('Asia/Kolkata').format('YYYY-MM-DD');
      
      let validAppointments: any[] = [];
      let validAppointmentIds: any[] = [];
      
      // Fetch all appointments for status counts and order filtering
      let allTodayAppointments: any[] = [];
      
      try {
        const todayStart = dayjs().tz('Asia/Kolkata').startOf('day').toISOString();
        const todayEnd = dayjs().tz('Asia/Kolkata').endOf('day').toISOString();
        
        // Fetch all appointment statuses for comprehensive stats
        const allStatuses = [
          AppointmentStatus.SCHEDULED,
          AppointmentStatus.CONFIRMED, 
          AppointmentStatus.ARRIVED, 
          AppointmentStatus.IN_PROGRESS,
          AppointmentStatus.COMPLETED,
          AppointmentStatus.CANCELLED,
          AppointmentStatus.NO_SHOW,
          AppointmentStatus.RESCHEDULED
        ];
        
        for (const status of allStatuses) {
          try {
            const appointmentsResponse = await appointmentApi.getAll({
              startDate: todayStart,
              endDate: todayEnd,
              status: [status]
            });
            
            if (appointmentsResponse.data && appointmentsResponse.data.length > 0) {
              allTodayAppointments.push(...appointmentsResponse.data);
              
              // Also collect valid appointments for order filtering
              if ([AppointmentStatus.CONFIRMED, AppointmentStatus.ARRIVED, AppointmentStatus.COMPLETED].includes(status)) {
                validAppointments.push(...appointmentsResponse.data);
              }
            }
          } catch (statusError) {
            console.warn(`Failed to fetch ${status} appointments:`, statusError);
          }
        }
        
        validAppointmentIds = validAppointments.map((appt: any) => appt.id);
        
        // Calculate appointment status counts
        const totalAppointments = allTodayAppointments.length;
        const totalScheduled = allTodayAppointments.filter(appt => 
          ![AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW].includes(appt.status)
        ).length;
        const totalArrived = allTodayAppointments.filter(appt => 
          [AppointmentStatus.ARRIVED, AppointmentStatus.CONFIRMED, AppointmentStatus.COMPLETED].includes(appt.status)
        ).length;
        const totalCancelledNoShow = allTodayAppointments.filter(appt => 
          [AppointmentStatus.CANCELLED, AppointmentStatus.NO_SHOW].includes(appt.status)
        ).length;
        
        console.log('--- APPOINTMENT STATS ---');
        console.log('Total Appointments:', totalAppointments);
        console.log('Total Scheduled:', totalScheduled);
        console.log('Total Arrived:', totalArrived);
        console.log('Total Cancelled/No Show:', totalCancelledNoShow);
        
        setAppointmentStats({
          totalAppointments,
          totalScheduled,
          totalArrived,
          totalCancelledNoShow
        });
        
      } catch (appointmentError) {
        console.warn('Failed to fetch appointments, proceeding with orders only:', appointmentError);
        setAppointmentStats({
          totalAppointments: 0,
          totalScheduled: 0,
          totalArrived: 0,
          totalCancelledNoShow: 0
        });
      }
      
      console.log('Valid appointments (CONFIRMED, ARRIVED, COMPLETED):', validAppointmentIds.length);
      console.log('Valid appointment IDs:', validAppointmentIds);
      
      // Get all orders for today
      const allOrders = await appointmentOrdersApi.getOrdersByDateRange(today, today);
      console.log('All orders for today:', allOrders.length);
      allOrders.forEach((order: any) => {
        console.log('Order:', order.orderNumber, 'appointmentId:', order.appointmentId, 'financialStatus:', order.financialStatus);
      });
      
      // Filter orders to only include those from valid appointments
      // Also include orders without appointmentId (standalone orders)
      const orders = validAppointmentIds.length > 0 
        ? allOrders.filter((order: any) => 
            validAppointmentIds.includes(order.appointmentId) || !order.appointmentId
          )
        : allOrders;
        
      console.log('Filtered orders:', orders.length);
      orders.forEach((order: any) => {
        console.log('Filtered Order:', order.orderNumber, 'appointmentId:', order.appointmentId, 'financialStatus:', order.financialStatus);
      });
      
      console.log('--- FILTERED ORDERS (from valid appointments) ---');
      orders.forEach((order: any) => {
        console.log(
          '[ORDER]',
          order.orderNumber,
          'appointmentId:', order.appointmentId,
          'financialStatus:', order.financialStatus,
          'grandTotal:', order.grandTotal,
          'paidAmount:', order.paidAmount
        );
      });
      
      let totalOrder = 0;
      let totalBilled = 0;
      let totalPaid = 0;

      // Calculate metrics based on filtered orders
      orders.forEach((order: any) => {
        const financialStatus = (order.financialStatus || '').toUpperCase().trim();
        
        // Total Orders: exclude CANCELLED and REFUNDED
        if (!['CANCELLED', 'REFUNDED'].includes(financialStatus)) {
          totalOrder += order.grandTotal || 0;
        }
        
        // Total Billed: include only BILLED, PAID, PARTIALLY_PAID
        if (['BILLED', 'PAID', 'PARTIALLY_PAID'].includes(financialStatus)) {
          totalBilled += order.grandTotal || 0;
        }
        
        // Total Paid: include only PAID and PARTIALLY_PAID
        if (['PAID', 'PARTIALLY_PAID'].includes(financialStatus)) {
          totalPaid += order.paidAmount || 0;
        }
      });

      // Total Pending = Total Billed - Total Paid
      const outstanding = totalBilled - totalPaid;
      
      // Calculate unbilled amount (Total Orders - Total Billed)
      const unbilledAmount = totalOrder - totalBilled;

      console.log('--- CALCULATED STATS ---');
      console.log('Total Orders:', totalOrder);
      console.log('Total Billed:', totalBilled);
      console.log('Unbilled Amount:', unbilledAmount);
      console.log('Total Paid:', totalPaid);
      console.log('Total Pending:', outstanding);

      setStats({
        totalOrders: totalOrder,
        totalBilled,
        totalPaid,
        outstanding,
        unbilledAmount,
        statusCounts: stats.statusCounts,
      });
    } catch (e: any) {
      console.error('Error in fetchStats:', e);
      // Set stats to zero values instead of showing error
      setStats({
        totalOrders: 0,
        totalBilled: 0,
        totalPaid: 0,
        outstanding: 0,
        unbilledAmount: 0,
        statusCounts: {
          BILLED: 0,
          PAID: 0,
          PARTIALLY_PAID: 0,
          ORDERED: 0,
          REFUNDED: 0,
          CANCELLED: 0,
        },
      });
      setError(null); // Don't show error to user, just log it
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Hospital Dashboard
      </Typography>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/financial')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <OrdersIcon color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Total Orders</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'primary.main', fontWeight: 'bold' }}>{stats.totalOrders}</Typography>
              </Paper>
            </ButtonBase>
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/patient-statement')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <BillingIcon color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Total Billed</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'primary.main', fontWeight: 'bold' }}>
                  {stats.totalBilled.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  {stats.unbilledAmount > 0 && (
                    <Typography component="span" variant="h6" sx={{ color: 'warning.main', ml: 1 }}>
                      (₹{stats.unbilledAmount.toLocaleString('en-IN')} unbilled)
                    </Typography>
                  )}
                </Typography>
              </Paper>
            </ButtonBase>
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/collection')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <PaidIcon color="success" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Total Paid</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'success.main', fontWeight: 'bold' }}>{stats.totalPaid.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</Typography>
              </Paper>
            </ButtonBase>
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/patient-statement')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <PendingIcon color="warning" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Outstanding</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'warning.main', fontWeight: 'bold' }}>
                  {stats.outstanding.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  <Typography component="span" variant="h6" sx={{ color: 'text.secondary', ml: 1 }}>
                    (₹{(stats.totalOrders - stats.totalPaid).toLocaleString('en-IN')})
                  </Typography>
                </Typography>
              </Paper>
            </ButtonBase>
          </Grid>
        </Grid>
        )}
        
      {/* Appointment Status Cards */}
      {!loading && !error && (
        <Grid container spacing={4} sx={{ mb: 4 }}>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/appointments')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <AppointmentIcon color="info" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Total Appointments</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'info.main', fontWeight: 'bold' }}>{appointmentStats.totalAppointments}</Typography>
              </Paper>
            </ButtonBase>
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/appointments')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <ScheduledIcon color="primary" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Total Scheduled</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'primary.main', fontWeight: 'bold' }}>{appointmentStats.totalScheduled}</Typography>
              </Paper>
            </ButtonBase>
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/appointments')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <ArrivedIcon color="success" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Total Arrived</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'success.main', fontWeight: 'bold' }}>{appointmentStats.totalArrived}</Typography>
              </Paper>
            </ButtonBase>
          </Grid>
          <Grid item xs={12} md={3}>
            <ButtonBase
              onClick={() => navigate('/reports/appointments')}
              sx={{ width: '100%', textAlign: 'left' }}
            >
              <Paper sx={{ p: 4, textAlign: 'center', width: '100%', '&:hover': { bgcolor: 'action.hover' } }} elevation={3}>
                <CancelledIcon color="error" sx={{ fontSize: 48 }} />
                <Typography variant="h5" sx={{ mt: 2, fontWeight: 'medium' }}>Cancelled/No Show</Typography>
                <Typography variant="h3" sx={{ mt: 1, color: 'error.main', fontWeight: 'bold' }}>{appointmentStats.totalCancelledNoShow}</Typography>
              </Paper>
            </ButtonBase>
          </Grid>
        </Grid>
      )}
      

      {/* Recent Activity */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Recent Appointments
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {recentAppointments.length === 0 ? (
              <Box sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                <Typography>No recent appointments</Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Schedule new appointments to see them here
                </Typography>
              </Box>
            ) : (
              <Box>
                {recentAppointments.map((appt: any, idx: number) => (
                  <Box key={appt.id || idx} sx={{ mb: 2, p: 2, border: '1px solid #eee', borderRadius: 1 }}>
                    <Typography variant="subtitle1">
                      {appt.patientName} &bull; {appt.doctorName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {appt.date} {appt.time} &mdash; <b>{appt.status}</b>
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Notifications
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
              <Typography>No new notifications</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HospitalDashboard;
